import * as Api from '@tableau/api-internal-contract-js';
import { ExtensionMode, WorkbookFormatting } from '@tableau/api-internal-contract-js';
import { TextStyleFormattingConvertersFactory } from '@tableau/css-fonts-fallback';
import * as PresLayerEnums from '../../gen-src/preslayer/Enums';
import * as PresLayer from '../../gen-src/preslayer/PresModels';
import { ApiContext } from '../ApiContext';
import { ErrorHelpers } from '../ErrorHelpers';
import { PresLayerToApiEnumMappings as EnumMappings } from './EnumMappings';
import { PresLayerOptionalCompatibility } from './PresLayerOptionalCompatibility';
import { PresLayerToSharedApiConverter } from './PresLayerToSharedApiConverter';

/**
 * Class containing helper methods for converting from PresLayerPresModels to their ExtensionsApi PresModels equivalents
 *
 * @export
 * @class PresLayerToExtensionsApiConverter
 */
export class PresLayerToExtensionsApiConverter {
  public static PARAMETERS_DB_NAME = 'Parameters';

  public static convertSheetPath(plSheetPath: PresLayer.SheetPathPresModel): Api.SheetPath {
    const result: Api.SheetPath = {
      sheetName: plSheetPath.sheetName,
      isDashboard: plSheetPath.isDashboard,
      storyboard: plSheetPath.storyboard,
      flipboardZoneID: 0,
      storyPointID: plSheetPath.storyPointId,
    };

    return result;
  }

  public static convertExtensionLocator(plAddInLocator: PresLayer.AddInLocatorPresModel): Api.ExtensionLocator {
    if (plAddInLocator.visualIdPresModel) {
      const result: Api.WorksheetExtensionLocator = {
        instanceId: plAddInLocator.addInInstanceId,
        visualId: PresLayerToSharedApiConverter.convertVisualId(plAddInLocator.visualIdPresModel),
      };
      return result;
    }

    if (plAddInLocator.sheetPath) {
      const result: Api.DashboardExtensionLocator = {
        instanceId: plAddInLocator.addInInstanceId,
        dashboardPath: PresLayerToExtensionsApiConverter.convertSheetPath(plAddInLocator.sheetPath),
      };
      return result;
    }

    const result: Api.InvalidExtensionLocator = {
      instanceId: plAddInLocator.addInInstanceId,
    };
    return result;
  }

  public static convertDashboardInfo(plDashboardInfo: PresLayer.AddInDashboardInfoPresModel): Api.ExtensionDashboardInfo {
    const result: Api.ExtensionDashboardInfo = {
      zones: PresLayerToSharedApiConverter.convertDashboardToZones(plDashboardInfo.dashboardZoneInfos),
      name: PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(plDashboardInfo.sheetPath).sheetName,
      extensionZoneId: plDashboardInfo.zoneId,
      size: PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(plDashboardInfo.viewportSize),
      activeDashboardObjectId: plDashboardInfo.activeZoneId,
    };

    return result;
  }

  public static convertWorksheetInfo(plWorksheetInfo: PresLayer.ExtensionWorksheetInfoPresModel): Api.ExtensionWorksheetInfo {
    let worksheetFormatting: WorkbookFormatting | undefined = plWorksheetInfo.workbookFormatting
      ? PresLayerToExtensionsApiConverter.convertWorkbookFormattingPresModel(plWorksheetInfo.workbookFormatting)
      : undefined;
    if (!worksheetFormatting) worksheetFormatting = { formattingSheets: [] };

    const result: Api.ExtensionWorksheetInfo = {
      visualId: PresLayerToSharedApiConverter.convertVisualId(plWorksheetInfo.visualIdPresModel),
      backgroundColor: plWorksheetInfo.color,
      formatting: worksheetFormatting,
      size: plWorksheetInfo.viewportSize,
    };

    return result;
  }

  public static convertDashboardLayoutChangedEvent(
    dashboardLayoutEvent: PresLayer.DashboardLayoutChangedEventPresModel,
  ): Api.DashboardLayoutEvent {
    const result: Api.DashboardLayoutEvent = {
      zones: PresLayerToSharedApiConverter.convertDashboardToZones(dashboardLayoutEvent.dashboardZoneInfos),
      activeZoneId: dashboardLayoutEvent.activeZoneId ?? 0,
      filterByLocator: true,
      locator: PresLayerToExtensionsApiConverter.convertExtensionLocator(dashboardLayoutEvent.addInLocatorPresModel),
      activeDashboardName: dashboardLayoutEvent.addInLocatorPresModel.sheetPath?.sheetName ?? '',
    };

    return result;
  }

  public static convertExtensionInstance(plAddInInstance: PresLayer.AddInInstancePresModel): Api.ExtensionInstance {
    const result: Api.ExtensionInstance = {
      url: PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(plAddInInstance.addInRegistrationPresModel).url,
      locator: PresLayerToExtensionsApiConverter.convertExtensionLocator(plAddInInstance.addInLocatorPresModel),
    };

    return result;
  }

  public static convertExtensionEnvironment(plAddInEnvironment: PresLayer.AddInEnvironmentPresModel): Api.ExtensionEnvironment {
    const tableauServerSiteName: string | undefined = plAddInEnvironment.tableauServerSiteName;
    const featureFlags: string[] | undefined = plAddInEnvironment.extensionFeatureFlags;
    const result: Api.ExtensionEnvironment = {
      extensionContext: EnumMappings.extensionContext.convert(plAddInEnvironment.addInContext),
      extensionMode: EnumMappings.extensionMode.convert(plAddInEnvironment.addInMode),
      extensionLocale: plAddInEnvironment.addInLocale,
      extensionLanguage: plAddInEnvironment.addInLanguage,
      extensionCountry: plAddInEnvironment.addInCountry,
      tableauVersion: plAddInEnvironment.tableauVersion,
      operatingSystem: plAddInEnvironment.operatingSystem,
      apiVersion: plAddInEnvironment.apiVersion,
      userId: plAddInEnvironment.userLuid.length > 0 ? plAddInEnvironment.userLuid : undefined,
      siteId: plAddInEnvironment.siteLuid.length > 0 ? plAddInEnvironment.siteLuid : undefined,
      siteNamespace: tableauServerSiteName && tableauServerSiteName.length > 0 ? tableauServerSiteName : undefined,
      workbookId: plAddInEnvironment.workbookId.length > 0 ? plAddInEnvironment.workbookId : undefined,
      platformVersion: Api.INTERNAL_CONTRACT_VERSION,
      workbookFormatting: PresLayerToExtensionsApiConverter.convertWorkbookFormattingPresModel(
        PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(plAddInEnvironment.workbookFormatting),
      ),
      uniqueUserId: plAddInEnvironment.encryptedUserName,
      featureFlags: featureFlags && featureFlags.length > 0 ? featureFlags : undefined,
    };

    // this information is not available in our tabdoc commands, it's passed in as part of server bootstrap
    const apiContext = ApiContext.getInstance();
    if (apiContext.isServer() && apiContext.isAuthoring()) {
      result.extensionMode = ExtensionMode.Authoring;
    }

    return result;
  }

  public static convertExtensionSettingsInfo(plAddInSettings: PresLayer.AddInSettingsInfoPresModel): Api.ExtensionSettingsInfo {
    const result: Api.ExtensionSettingsInfo = {
      // addInSettings is undefined during bootstrap initialization
      settingsValues: plAddInSettings.addInSettings || {},
    };

    return result;
  }

  public static convertExtensionBootstrapInfo(plBootstrapInfo: PresLayer.AddInBootstrapInfoPresModel): Api.ExtensionBootstrapInfo {
    let extensionDashboardInfo: Api.ExtensionDashboardInfo | undefined = undefined;
    if (plBootstrapInfo.addInDashboardInfoPresModel) {
      extensionDashboardInfo = PresLayerToExtensionsApiConverter.convertDashboardInfo(plBootstrapInfo.addInDashboardInfoPresModel);
    }

    let extensionWorksheetInfo: Api.ExtensionWorksheetInfo | undefined = undefined;
    if (plBootstrapInfo.extensionWorksheetInfoPresModel) {
      extensionWorksheetInfo = PresLayerToExtensionsApiConverter.convertWorksheetInfo(plBootstrapInfo.extensionWorksheetInfoPresModel);
    }

    const result: Api.ExtensionBootstrapInfo = {
      extensionDashboardInfo: extensionDashboardInfo,
      extensionWorksheetInfo: extensionWorksheetInfo,
      extensionEnvironment: PresLayerToExtensionsApiConverter.convertExtensionEnvironment(plBootstrapInfo.addInEnvironmentPresModel),
      extensionInstance: PresLayerToExtensionsApiConverter.convertExtensionInstance(plBootstrapInfo.addInInstancePresModel),
      extensionSettingsInfo: PresLayerToExtensionsApiConverter.convertExtensionSettingsInfo(plBootstrapInfo.addInSettingsInfo),
      extensionDialogPayload: plBootstrapInfo.extensionDialogPayload,
    };

    return result;
  }

  public static convertWorkbookFormattingChangedPresModel(
    plWorkbookFormatting: PresLayer.WorkbookFormattingChangedPresModel,
  ): Api.WorkbookFormattingEvent | undefined {
    const workbookFormattingPresModel = PresLayerToExtensionsApiConverter.convertWorkbookFormattingPresModel(
      plWorkbookFormatting.workbookFormatting,
    );

    if (!workbookFormattingPresModel) {
      return undefined;
    }

    const workbookFormattingEvent: Api.WorkbookFormattingEvent = {
      formattingSheets: workbookFormattingPresModel.formattingSheets,
      filterByLocator: true,
      locator: PresLayerToExtensionsApiConverter.convertExtensionLocator(plWorkbookFormatting.addInLocatorPresModel),
    };

    return workbookFormattingEvent;
  }

  public static convertWorksheetFormattingChangedPresModel(
    plWorksheetFormattingChangedEvent: PresLayer.WorksheetFormattingChangedPresModel,
  ): Api.WorksheetFormattingEvent | undefined {
    const workbookFormattingPresModel = PresLayerToExtensionsApiConverter.convertWorkbookFormattingPresModel(
      plWorksheetFormattingChangedEvent.workbookFormatting,
    );

    if (!workbookFormattingPresModel) {
      return undefined;
    }

    const worksheetFormattingEvent: Api.WorksheetFormattingEvent = {
      formattingSheets: workbookFormattingPresModel.formattingSheets,
      visualId: PresLayerToSharedApiConverter.convertVisualId(plWorksheetFormattingChangedEvent.visualIdPresModel),
    };

    return worksheetFormattingEvent;
  }

  public static convertWorkbookFormattingPresModel(
    plWorkbookFormatting: PresLayer.WorkbookFormattingPresModel,
  ): Api.WorkbookFormatting | undefined {
    if (!plWorkbookFormatting) {
      return undefined;
    }
    const formattingMap: Array<Api.FormattingSheet> = plWorkbookFormatting.formattingSheets.map((currentFormattingSheet) => {
      const formattingSheet: Api.FormattingSheet = {
        classNameKey: EnumMappings.classNameKey.convert(currentFormattingSheet.classNameKey),
        cssProperties: getCssProperties(currentFormattingSheet.classNameKey, currentFormattingSheet),
      };

      TextStyleFormattingConvertersFactory.cleanUpFontFamilyStylingForCSSProperties(formattingSheet.cssProperties);
      return formattingSheet;
    });
    const result: Api.WorkbookFormatting = {
      formattingSheets: formattingMap,
    };
    return result;
  }

  public static convertDialogUpdateEvent(plDialogUpdateEvent: PresLayer.ExtensionDialogEventPresModel): Api.DialogUpdateEvent {
    const result: Api.DialogUpdateEvent = {
      isCloseEvent: plDialogUpdateEvent.isExtensionCloseEvent,
      closePayload: plDialogUpdateEvent.extensionDialogPayload,
    };

    return result;
  }

  public static convertSettingsChangedEvent(settingsEvent: PresLayer.SettingsChangedEventPresModel): Api.SettingsEvent {
    const result: Api.SettingsEvent = {
      newSettings: settingsEvent.addInSettingsInfo.addInSettings,
      filterByLocator: true,
      locator: PresLayerToExtensionsApiConverter.convertExtensionLocator(settingsEvent.addInLocatorPresModel),
    };

    return result;
  }

  public static convertContextMenuClickEvent(clickEvent: PresLayer.ContextMenuEventPresModel): Api.ContextMenuEvent {
    const result: Api.ContextMenuEvent = {
      id: clickEvent.extensionContextMenuId,
      filterByLocator: true,
      locator: PresLayerToExtensionsApiConverter.convertExtensionLocator(clickEvent.addInLocatorPresModel),
    };

    return result;
  }

  public static convertExtensionDialogResult(plResult: PresLayerEnums.ExtensionDialogResult): Api.ExtensionDialogResult {
    return EnumMappings.extensionDialogResult.convert(plResult);
  }

  public static convertDataSchema(dataSchema: PresLayer.DataSchemaPresModel): Api.DataSchema {
    const apiDataSources: Api.DataSourceMap = {};

    ErrorHelpers.verifyValue(dataSchema, 'dataSchema');
    ErrorHelpers.verifyValue(dataSchema.dataSources, 'dataSchema.dataSources');

    const dataSources = dataSchema.dataSources!;

    // Convert each pres layer datasource in the schema to an Api datasource
    Object.keys(dataSources).forEach((key) => {
      // Skip the default Parameters datasource we receive from the platform.
      if (dataSources[key].datasource !== PresLayerToExtensionsApiConverter.PARAMETERS_DB_NAME) {
        apiDataSources[key] = PresLayerToExtensionsApiConverter.convertDataSource(dataSources[key]);
      }
    });

    const schemaMap = PresLayerToExtensionsApiConverter.convertWorksheetDataSchemaMap(dataSchema.worksheetDataSchemaMap);

    const result: Api.DataSchema = {
      dataSources: apiDataSources,
      worksheetDataSchemaMap: schemaMap,
    };

    return result;
  }

  public static convertDataSource(dataSource: PresLayer.DataSourcePresModel): Api.DataSource {
    const extractUpdateTime = dataSource.extractUpdateTime === '' ? undefined : dataSource.extractUpdateTime;
    const publishedUrl = dataSource.publishedUrl && ApiContext.getInstance().isFirstClass() ? dataSource.publishedUrl : undefined;
    const dataSourceFields = new Array<Api.Field>();

    ErrorHelpers.verifyValue(dataSource, 'dataSource');
    ErrorHelpers.verifyValue(dataSource.fieldList, 'dataSchema.fieldList');

    dataSource.fieldList.forEach((plField) => {
      const hierarchyModel = plField as PresLayer.HierarchyPresModel;

      // If the datasource contains hierarchies or folders, the base field will contain
      // a list of the fields grouped beneath it as 'columnList'
      // Tableau does not support nested folders or hierarchies, so this does not need to be recursive.
      if (hierarchyModel.columnList) {
        // convert each field in the hierarchy to the external API definition
        hierarchyModel.columnList.forEach((field) =>
          dataSourceFields.push(PresLayerToExtensionsApiConverter.convertHierarchalField(field)),
        );
      } else {
        // ignore the fields that were already handled by the hierarchical field logic
        if (plField.type !== PresLayerEnums.SchemaItemType.FieldFolder && plField.type !== PresLayerEnums.SchemaItemLocatorType.Field) {
          dataSourceFields.push(PresLayerToSharedApiConverter.convertField(plField));
        }
      }
    });

    const result: Api.DataSource = {
      name: dataSource.datasourceCaption,
      id: dataSource.datasource,
      isExtract: dataSource.isExtract,
      extractUpdateTime: extractUpdateTime,
      fields: dataSourceFields,
      isPublished: dataSource.isPublished,
      publishedUrl: publishedUrl,
    };

    return result;
  }

  public static convertWorksheetDataSchemaMap(schemaMap: {
    [key: string]: PresLayer.WorksheetDataSchemaPresModel;
  }): Api.WorksheetDataSchemaMap {
    const result: Api.WorksheetDataSchemaMap = {};

    if (schemaMap) {
      Object.keys(schemaMap).forEach((key) => {
        // We need to remove any references to the "Parameters" datasource.  This is treated as a separate
        // datasource by the platform, but the Extensions API accesses Parameters directly.
        const cleanReferencedDataSourceList = schemaMap[key].referencedDataSourceList.filter((dsName) => {
          return dsName !== PresLayerToExtensionsApiConverter.PARAMETERS_DB_NAME;
        });

        const worksheetDataSourceInfo: Api.WorksheetDataSourceInfo = {
          primaryDataSource: schemaMap[key].primaryDatasource,
          referencedDataSourceList: cleanReferencedDataSourceList,
        };

        result[key] = worksheetDataSourceInfo;
      });
    }

    return result;
  }

  public static convertHierarchalField(plField: PresLayer.FieldPresModel): Api.Field {
    const fieldColumn = plField as PresLayer.FieldColumnPresModel;

    // set the default aggregation to match the aggregation specific to this level
    // (this will be undefined if the field is a hierarchical menu from a filter)
    fieldColumn.defaultAggregation = fieldColumn.aggregation;
    fieldColumn.defaultFieldRole = fieldColumn.fieldRole;

    return PresLayerToSharedApiConverter.convertField(fieldColumn);
  }

  public static convertJoinDescription(plJoinDescription: PresLayer.JoinDescriptionPresModel): Api.JoinDescription {
    ErrorHelpers.verifyValue(plJoinDescription, 'plJoinDescription');
    const apiTables: Array<Api.TableInfo> = plJoinDescription.joinTableInfos.map((joinTable) => {
      const apiTable: Api.TableInfo = {
        name: joinTable.tableAlias,
        id: joinTable.tableName,
        customSQL: joinTable.isCustomSql ? joinTable.sqlQuery : undefined,
        connectionClassName: joinTable.connectionClassName,
        connectionId: joinTable.connectionName,
      };

      return apiTable;
    });

    const result: Api.JoinDescription = {
      tables: apiTables,
    };

    return result;
  }

  public static convertActiveTables(plActiveTableInfos: PresLayer.ActiveTableInfoPresModel[]): Api.TableInfos {
    ErrorHelpers.verifyValue(plActiveTableInfos, 'plActiveTableInfos');
    const apiTables: Array<Api.TableInfo> = plActiveTableInfos.map((activeTable) => {
      const apiTable: Api.TableInfo = {
        name: activeTable.tableAlias,
        id: activeTable.tableName,
        customSQL: activeTable.isCustomSql ? activeTable.sqlQuery : undefined,
        connectionClassName: activeTable.connectionClassName,
        connectionId: activeTable.connectionName,
      };

      return apiTable;
    });

    const result: Api.TableInfos = {
      tables: apiTables,
    };

    return result;
  }

  public static convertConnectionDescrptionSummaries(
    plConnectionDescription: PresLayer.ConnectionDescriptionSummaryPresModel[],
  ): Api.ConnectionDescriptionSummary[] {
    return plConnectionDescription.map((plSummary) => {
      const apiConnection: Api.ConnectionDescriptionSummary = {
        name: plSummary.connectionCaption,
        id: plSummary.connectionName,
        type: plSummary.connectionTypeLabel,
        serverURI: plSummary.connectionUri,
      };

      return apiConnection;
    });
  }

  public static convertShowDataTableObjectList(plTableObjects: PresLayer.ShowDataTableIdWithCaptionPresModel[]): Api.LogicalTableInfo[] {
    return plTableObjects.map((plTableObject) => {
      const logicalTableInfo: Api.LogicalTableInfo = {
        id: plTableObject.showDataTableId,
        caption: plTableObject.showDataTableCaption,
      };

      return logicalTableInfo;
    });
  }
}
function getLineStyle(linePattern: PresLayerEnums.LinePattern) {
  switch (linePattern) {
    case PresLayerEnums.LinePattern.LinePatternNone:
      return 'none';
    case PresLayerEnums.LinePattern.LinePatternSolid:
      return 'solid';
    case PresLayerEnums.LinePattern.LinePatternDashed:
      return 'dashed';
    case PresLayerEnums.LinePattern.LinePatternDotted:
      return 'dotted';

    default:
      return undefined;
  }
}

function getCssProperties(classNameKey: PresLayerEnums.ClassNameKey, currentFormattingSheet: PresLayer.FormattingSheetPresModel) {
  if (classNameKey === PresLayerEnums.ClassNameKey.RowDividers || classNameKey === PresLayerEnums.ClassNameKey.ColumnDividers) {
    return {
      borderColor: currentFormattingSheet.color,
      borderStyle: getLineStyle(currentFormattingSheet.linePattern),
      borderWidth: currentFormattingSheet.strokeWidth + 'pt',
    } as React.CSSProperties;
  }

  return {
    fontFamily: "'" + currentFormattingSheet.fontName + "'",
    fontSize: currentFormattingSheet.fontSize + 'pt',
    fontWeight: currentFormattingSheet.isFontBold ? 'bold' : 'normal',
    fontStyle: currentFormattingSheet.isFontItalic ? 'italic' : 'normal',
    textDecoration: currentFormattingSheet.isFontUnderlined ? 'underline' : 'none',
    color: currentFormattingSheet.color,
  } as React.CSSProperties;
}
